import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { consumosApi } from '@/services/api'
import type { ProduccionCabecera, PlanificacionSemanalRow } from '@/types'
import { pct, parseDate } from '@/utils/planningFormatters'
import { cuentaComoRotura } from '@/utils/estadoMaterial'

export type PlanningMode = 'weekly' | 'monthly' | 'yearly'

/** Origen de datos de cada rango. Las claves de caché son distintas por modo para
 *  que React Query mantenga los tres rangos por separado. */
const SOURCES: Record<PlanningMode, {
  prodKey: string
  prodFn: (offset: number) => ReturnType<typeof consumosApi.getProducciones>
  planKey: string
  planFn: (offset: number) => ReturnType<typeof consumosApi.getPlanificacionSemanal>
}> = {
  weekly: {
    prodKey: 'v2-producciones',   prodFn: (o) => consumosApi.getProducciones(o),
    planKey: 'v2-plan-semanal',   planFn: (o) => consumosApi.getPlanificacionSemanal(o),
  },
  monthly: {
    prodKey: 'mes-producciones',  prodFn: (o) => consumosApi.getProduccionesMes(o),
    planKey: 'mes-plan-mensual',  planFn: (o) => consumosApi.getPlanificacionMensual(o),
  },
  yearly: {
    prodKey: 'anio-producciones', prodFn: (o) => consumosApi.getProduccionesAnio(o),
    planKey: 'anio-plan-anual',   planFn: (o) => consumosApi.getPlanificacionAnual(o),
  },
}

export function usePlanningData(
  mode: PlanningMode,
  offset: number,
  capacidadMax: number,
) {
  const src = SOURCES[mode]
  const produccionesQ = useQuery({ queryKey: [src.prodKey, offset], queryFn: () => src.prodFn(offset) })
  const planQ         = useQuery({ queryKey: [src.planKey, offset], queryFn: () => src.planFn(offset) })

  const producciones: ProduccionCabecera[]      = produccionesQ.data?.data ?? []
  const planRows:     PlanificacionSemanalRow[]  = planQ.data?.data         ?? []
  const isLoading = produccionesQ.isLoading || planQ.isLoading

  const planByProd = useMemo(() => {
    const map = new Map<string, PlanificacionSemanalRow[]>()
    planRows.forEach(r => { const l = map.get(r.prodid) ?? []; l.push(r); map.set(r.prodid, l) })
    return map
  }, [planRows])

  const totalUnidades   = useMemo(() => producciones.reduce((s, p) => s + Number(p.unidades), 0), [producciones])
  const totalPendientes = useMemo(() => producciones.reduce((s, p) => s + Number(p.unidadesPendientes), 0), [producciones])
  const avanceGlobal    = pct(totalUnidades - totalPendientes, totalUnidades)
  const capacidadPct    = pct(totalUnidades, capacidadMax)

  const prodsConRotura = useMemo(
    () => new Set(planRows.filter(r => cuentaComoRotura(r)).map(r => r.prodid)),
    [planRows]
  )
  const coberturaPct = producciones.length > 0
    ? pct(producciones.length - prodsConRotura.size, producciones.length)
    : 100

  const unidadesEnRiesgo = useMemo(
    () => producciones
      .filter(p => prodsConRotura.has(p.produccion))
      .reduce((s, p) => s + Number(p.unidadesPendientes), 0),
    [producciones, prodsConRotura]
  )
  const riesgoPct = pct(unidadesEnRiesgo, totalPendientes)

  const proximasEntregas = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const in3   = new Date(today); in3.setDate(today.getDate() + 3)
    return producciones.filter(p => {
      const f = parseDate(p.fechaFin)
      return f && f >= today && f <= in3 && Number(p.unidadesPendientes) > 0
    }).length
  }, [producciones])

  return {
    producciones, planRows, isLoading, planByProd,
    totalUnidades, totalPendientes, avanceGlobal, capacidadPct,
    prodsConRotura, coberturaPct, unidadesEnRiesgo, riesgoPct, proximasEntregas,
  }
}
